import { useEffect, useState } from 'react'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useStats, useStatsT } from './statsContext'
import { StoryCard, StreamRow } from './Cards'
import { Delta, Empty, ErrorLine, Kv, ListBlock, Loading, MediaCount, Pills, ReadMore, TableHead, Thumb } from './StatsParts'
import { StatsChart } from './StatsChart'
import {
  formatDateTime,
  int,
  listKeyOf,
  messagesLoader,
  metricKind,
  money,
  offsetLoader,
  PAGE_SIZE,
  pillLabel,
  PILLS,
  plain,
  shortDuration,
  tipsText,
  withRange,
  type Raw,
} from './statisticsData'
import { useStat, useStatList } from './statsStore'

/** "12 Posts, $340.00" / "12 Posts, 1,024 Views" with the metric's change. */
function MetricHeader({ count, metric, by }: { count: string; metric: Raw | undefined; by: string }) {
  const { t } = useStatsT()
  const unit: Record<string, string> = { views: 'views', likes: 'likes', comments: 'comments' }
  const value = metricKind(by) === 'money' ? money(metric?.total) : `${int(metric?.total)} ${unit[by] ? t(`l.${unit[by]}`) : ''}`.trim()
  return (
    <div className="ofx-chart-head">
      <strong>
        {count}, {value}
      </strong>
      <Delta value={metric?.delta} />
    </div>
  )
}

/** The "FULL STAT" dialog, from the row's own stats (as the CRM page does). */
function PostStatsDialog({ post, onClose }: { post: Raw; onClose: () => void }) {
  const { t, intl } = useStatsT()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const s = (post.stats ?? {}) as Raw
  const rows: [string, string][] = [
    ['views', int(s.lookCount)],
    ['uniqueViews', int(s.uniqueLookCount)],
    ['viewDuration', shortDuration(s.lookDuration)],
    ['averageViewDuration', `${Number(s.lookDurationAverage || 0).toFixed(1)}s`],
    ['likes', int(s.likeCount)],
    ['comments', int(s.commentCount)],
    ['tips', `${int(s.tipCount)} (${money(s.tipSum)})`],
    ['purchases', `${int(s.purchasedCount)} (${money(s.purchasedSumm)})`],
  ]
  if (s.hasVideo) rows.push(['videoPlays', int(s.videoPlayCount)], ['videoPlayTime', shortDuration(s.videoPlayDuration)])
  return (
    <div className="ofp-modal-back" onClick={onClose}>
      <div className="ofp-modal" role="dialog" aria-modal="true" aria-label={t('postStatistics')} onClick={(e) => e.stopPropagation()}>
        <div className="ofp-modal-head">
          <h3 className="ofp-modal-title">{t('postStatistics')}</h3>
          <button type="button" className="lib-icon-btn" aria-label={t('close')} onClick={onClose}>
            <FeatherIcon name="x" size={18} />
          </button>
        </div>
        <div className="ofx-muted-line">{formatDateTime(post.postedAt, intl)}</div>
        <div>
          {rows.map(([k, v]) => (
            <Kv key={k} label={t(`l.${k}`)} value={v} />
          ))}
        </div>
        <div className="ofp-modal-actions">
          <button type="button" className="lib-btn lib-btn--primary" onClick={onClose}>
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export function PostsMain() {
  const { t, intl } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const [statsOf, setStatsOf] = useState<Raw | null>(null)
  const by = pills.posts
  const chart = useStat(store, 'posts/chart', withRange(range, { withTotal: 'true', [`filter[${by}]`]: by, 'filter[posts]': 'posts' }))
  const listKey = listKeyOf(`posts/top:${by}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'posts/top', withRange(range, { by, skip_users: 'all' }), 'items'))
  const d = chart.data ?? {}
  return (
    <>
      <Pills items={PILLS.posts} active={by} onPick={(id) => setPill('posts', id)} label={t('l.posts')} />
      <div className="ofx-block">
        {chart.status === 'loading' ? (
          <Loading />
        ) : chart.status === 'error' ? (
          <ErrorLine message={chart.error} />
        ) : (
          <>
            <MetricHeader count={t('countPosts', { n: int((d.posts as Raw | undefined)?.total, false) })} metric={d[by] as Raw | undefined} by={by} />
            <StatsChart
              main={{ points: (d[by] as Raw | undefined)?.chart, kind: metricKind(by), label: t(`l.${pillLabel(PILLS.posts, by)}`) }}
              sub={{ points: (d.posts as Raw | undefined)?.chart, kind: 'count', label: t('l.posts') }}
            />
          </>
        )}
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.posts')}
          header={
            <TableHead cols="content">
              <span>{t('l.date')}</span>
              <span className="num">{t('l.views')}</span>
              <span className="num">{t('l.tips')}</span>
              <span className="num">{t('l.price')}</span>
              <span className="num">{t('l.purchases')}</span>
            </TableHead>
          }
        >
          {list.rows.map((post, i) => {
            const s = (post.stats ?? {}) as Raw
            return (
              <div key={String(post.id ?? i)} className="ofx-row">
                <div className="ofx-row-grid ofx-cols-content">
                  <strong>{formatDateTime(post.postedAt, intl)}</strong>
                  <span className="num">{int(s.lookCount)}</span>
                  <span className="num">{tipsText(post.tipsAmount, s.tipSum)}</span>
                  <span className="num">{Number(post.price) > 0 ? money(post.price) : '—'}</span>
                  <span className="num">{Number(s.purchasedCount) > 0 ? int(s.purchasedCount) : '—'}</span>
                </div>
                <div className="ofx-content-row">
                  <Thumb media={post.media} />
                  <div className="ofx-content-text">
                    <ReadMore id={`post-${post.id}`} text={String(post.rawText || plain(post.text))} />
                    <MediaCount media={post.media} count={post.mediaCount} />
                  </div>
                  <button type="button" className="ofx-link-btn" onClick={() => setStatsOf(post)}>
                    {t('fullStat')}
                  </button>
                </div>
              </div>
            )
          })}
        </ListBlock>
      </div>
      {statsOf && <PostStatsDialog post={statsOf} onClose={() => setStatsOf(null)} />}
    </>
  )
}

export function MessagesMain() {
  const { t, intl } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const kind = pills.messages === 'direct' ? 'direct' : 'group'
  const k = `${kind}_messages`
  const chart = useStat(store, 'messages/queue/chart', withRange(range, { withTotal: 'true', [`filter[${k}]`]: k, [`filter[${k}_purchases]`]: `${k}_purchases` }))
  const listKey = listKeyOf(`users/me/stats/messages/${kind}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, messagesLoader(store.call, range, kind))
  const d = chart.data ?? {}
  const purchases = d[`${k}_purchases`] as Raw | undefined
  return (
    <>
      <Pills items={PILLS.messages} active={kind} onPick={(id) => setPill('messages', id)} label={t('l.messages')} />
      <div className="ofx-block">
        {chart.status === 'loading' ? (
          <Loading />
        ) : chart.status === 'error' ? (
          <ErrorLine message={chart.error} />
        ) : (
          <>
            <div className="ofx-chart-head">
              <strong>
                {t('countMessages', { n: int((d[k] as Raw | undefined)?.total, false) })}, {money(purchases?.total)}
              </strong>
              <Delta value={purchases?.delta} />
            </div>
            <StatsChart
              main={{ points: purchases?.chart, kind: 'money', label: t('l.purchases') }}
              sub={{ points: (d[k] as Raw | undefined)?.chart, kind: 'count', label: t('l.messages') }}
            />
          </>
        )}
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.messages')}
          header={
            <TableHead cols="content">
              <span>{t('l.date')}</span>
              <span className="num">{t('l.sent')}</span>
              <span className="num">{t('l.viewed')}</span>
              <span className="num">{t('l.price')}</span>
              <span className="num">{t('l.purchases')}</span>
            </TableHead>
          }
        >
          {list.rows.map((msg, i) => (
            <div key={String(msg.id ?? i)} className="ofx-row">
              <div className="ofx-row-grid ofx-cols-content">
                <strong>{formatDateTime(msg.date || msg.createdAt, intl)}</strong>
                <span className="num">{int(msg.sentCount)}</span>
                <span className="num">{int(msg.viewedCount)}</span>
                <span className="num">{Number(msg.price) > 0 ? money(msg.price) : '—'}</span>
                <span className="num">{Number(msg.purchasedCount) > 0 ? int(msg.purchasedCount) : '—'}</span>
              </div>
              <div className="ofx-content-row">
                <Thumb media={msg.media} />
                <div className="ofx-content-text">
                  <ReadMore id={`msg-${msg.id}`} text={String(msg.rawText || plain(msg.text))} />
                  <MediaCount media={msg.media} count={msg.mediaCount} />
                </div>
              </div>
            </div>
          ))}
        </ListBlock>
      </div>
    </>
  )
}

export function StreamingMain() {
  const { t } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const by = pills.streaming
  const chart = useStat(store, 'streams/chart', withRange(range, { withTotal: 'true', [`filter[${by}]`]: by, 'filter[duration]': 'duration' }))
  const listKey = listKeyOf(`streams/top:${by}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'streams/top', withRange(range, { limit: PAGE_SIZE, by }), 'items'))
  const pillRow = <Pills items={PILLS.streaming} active={by} onPick={(id) => setPill('streaming', id)} label={t('l.streaming')} />
  if (chart.status === 'loading')
    return (
      <>
        {pillRow}
        <Loading />
      </>
    )
  if (chart.status === 'error')
    return (
      <>
        {pillRow}
        <ErrorLine message={chart.error} />
      </>
    )
  const d = chart.data ?? {}
  const dur = (d.duration ?? {}) as Raw
  const metric = d[by] as Raw | undefined
  if (!(Number(dur.total) > 0) && !(Number(metric?.total) > 0) && list.loaded && !list.rows.length)
    return (
      <>
        {pillRow}
        <Empty text={t('empty.streaming')} />
      </>
    )
  return (
    <>
      {pillRow}
      <div className="ofx-block">
        <MetricHeader count={t('countStreams', { n: shortDuration(dur.total) })} metric={metric} by={by} />
        <StatsChart
          main={{ points: metric?.chart, kind: metricKind(by), label: t(`l.${pillLabel(PILLS.streaming, by)}`) }}
          sub={{ points: dur.chart, kind: 'duration', label: t('l.duration') }}
        />
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock listKey={listKey} list={list} empty={t('empty.streaming')}>
          {list.rows.map((s, i) => (
            <StreamRow key={String(s.id ?? i)} stream={s} />
          ))}
        </ListBlock>
      </div>
    </>
  )
}

export function StoriesMain() {
  const { t } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const by = pills.stories
  const chart = useStat(store, 'stories/chart', withRange(range, { withTotal: 'true', [`filter[${by}]`]: by, 'filter[stories]': 'stories' }))
  const listKey = listKeyOf(`stories/top:${by}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'stories/top', withRange(range, { by }), 'items'))
  const d = chart.data ?? {}
  return (
    <>
      <Pills items={PILLS.stories} active={by} onPick={(id) => setPill('stories', id)} label={t('l.stories')} />
      <div className="ofx-block">
        {chart.status === 'loading' ? (
          <Loading />
        ) : chart.status === 'error' ? (
          <ErrorLine message={chart.error} />
        ) : (
          <>
            <MetricHeader count={t('countStories', { n: int((d.stories as Raw | undefined)?.total, false) })} metric={d[by] as Raw | undefined} by={by} />
            <StatsChart
              main={{ points: (d[by] as Raw | undefined)?.chart, kind: metricKind(by), label: t(`l.${pillLabel(PILLS.stories, by)}`) }}
              sub={{ points: (d.stories as Raw | undefined)?.chart, kind: 'count', label: t('l.stories') }}
            />
          </>
        )}
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock listKey={listKey} list={list} empty={t('empty.stories')}>
          <div className="ofx-story-grid">
            {list.rows.map((s, i) => (
              <StoryCard key={String(s.id ?? i)} story={s} />
            ))}
          </div>
        </ListBlock>
      </div>
    </>
  )
}
